import React, { ReactElement, cloneElement } from 'react';
import { useHasPermission, useHasAnyPermission, useHasAllPermissions, Permission } from 'contexts/PermissionContext';
import { Box, Typography } from '@mui/material';

interface PermissionGateProps {
  children: ReactElement | ReactElement[];
  permission?: Permission;
  permissions?: Permission[];
  requireAll?: boolean; // If true, requires ALL permissions. If false, requires ANY permission
  fallback?: ReactElement | null;
  renderUnauthorized?: boolean; // If true, shows a message instead of hiding content
}

/**
 * Permission Gate Component
 * Controls rendering based on user permissions
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  renderUnauthorized = false
}) => {
  const hasPermission = useHasPermission(permission!);
  const hasAnyPermission = useHasAnyPermission(permissions || []);
  const hasAllPermissions = useHasAllPermissions(permissions || []);

  let hasAccess = true;

  if (permission) {
    hasAccess = hasPermission;
  } else if (permissions && permissions.length > 0) {
    hasAccess = requireAll ? hasAllPermissions : hasAnyPermission;
  }

  if (!hasAccess) {
    if (renderUnauthorized) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            You don't have permission to access this content
          </Typography>
        </Box>
      );
    }
    return fallback;
  }

  return <>{children}</>;
};

interface PermissionWrapperProps {
  children: ReactElement;
  permission?: Permission;
  permissions?: Permission[];
  requireAll?: boolean;
  disableInsteadOfHide?: boolean; // If true, disables the component instead of hiding it
}

/**
 * Permission Wrapper Component
 * Wraps a component and either hides, disables, or renders it based on permissions
 */
export const PermissionWrapper: React.FC<PermissionWrapperProps> = ({
  children,
  permission,
  permissions,
  requireAll = false,
  disableInsteadOfHide = false
}) => {
  const hasPermission = useHasPermission(permission!);
  const hasAnyPermission = useHasAnyPermission(permissions || []);
  const hasAllPermissions = useHasAllPermissions(permissions || []);

  let hasAccess = true;

  if (permission) {
    hasAccess = hasPermission;
  } else if (permissions && permissions.length > 0) {
    hasAccess = requireAll ? hasAllPermissions : hasAnyPermission;
  }

  if (!hasAccess) {
    if (disableInsteadOfHide) {
      // Clone the child and add disabled prop
      return cloneElement(children, { 
        disabled: true, 
        sx: { 
          ...children.props.sx,
          opacity: 1,
          cursor: 'not-allowed'
        }
      });
    }
    return null; // Hide component
  }

  return children;
};

// Higher-order component for permission-based rendering
export const withPermission = <P extends object>(
  Component: React.ComponentType<P>,
  permission: Permission,
  fallback?: ReactElement | null
) => {
  return (props: P) => (
    <PermissionGate permission={permission} fallback={fallback}>
      <Component {...props} />
    </PermissionGate>
  );
};

// Higher-order component for permission-based rendering with multiple permissions
export const withPermissions = <P extends object>(
  Component: React.ComponentType<P>,
  permissions: Permission[],
  requireAll = false,
  fallback?: ReactElement | null
) => {
  return (props: P) => (
    <PermissionGate permissions={permissions} requireAll={requireAll} fallback={fallback}>
      <Component {...props} />
    </PermissionGate>
  );
};
